<template>
  <div class="md:px-16">
    <div
      v-if="isLoadingSupplierTypes"
      class="flex items-center justify-center px-16"
    >
      <JIcon name="spinner" size="40" />
    </div>
    <!-- MAIN HEADER -->
    <template v-else>
      <!-- FAMILY OR PROFILE NOT COMPLETE -->
      <FormNoEntries v-if="!hasUserProfileData" class="mt-16">
        <i18n-t keypath="profile.fillFamilyAndProfile" tag="div">
          <template #profile>
            <component
              :is="isMobile ? 'b' : 'button'"
              class="font-bold"
              :class="isMobile ? 'text-dark' : 'text-secondary-950 underline'"
              @click="setActiveProfileForm(0)"
            >
              {{ $t('profile.myProfile') }}
            </component>
          </template>
        </i18n-t>
      </FormNoEntries>

      <template v-else>
        <div
          v-show="!profileStore.isMobileSubFormOpen"
          class="mb-16 flex w-full items-center justify-between"
          :class="[tableItems.length && 'mb-16 md:mb-0']"
        >
          <h3 class="text-subject font-bold">{{ $t('profile.suppliers') }}</h3>

          <div class="flex flex-1 justify-end">
            <JButton
              v-show="
                (!profileStore.isMobileSubFormOpen && !isMobile) || isMobile
              "
              icon="solid_plus"
              size="sm"
              color="secondary"
              variant="tonal"
              type="button"
              :disabled="isLoading"
              @click="onAdd"
            >
              {{
                $t('profile.addNewSupplier', {
                  type:
                    activeSupplierType &&
                    $t(
                      `profile.supplierTypes.${activeSupplierType.title.toLowerCase()}`
                    ),
                })
              }}
            </JButton>
          </div>
        </div>

        <div
          v-show="profileStore.isMobileSubFormOpen && !editMode && !isMobile"
          class="mb-8 text-base font-semi-bold"
        >
          {{ $t('words.new') }}
          {{
            activeSupplierType &&
            $t(
              `profile.supplierTypes.${activeSupplierType.title.toLowerCase()}`
            )
          }}
          {{ $t('profile.suppliers').slice(0, -1) }}
        </div>

        <MyLoyaltyAccountsFormEditHeader
          v-if="editMode && profileStore.isMobileSubFormOpen && !isMobile"
          :supplierName="tableItems[selected].supplierName"
        />

        <!-- Desktop Form -->
        <JRow v-if="!isMobile" class="w-full">
          <MyLoyaltyAccountsFormFields
            v-if="profileStore.isMobileSubFormOpen"
            ref="formRef"
            :supplierType="activeSupplierType?.indicator!"
            :form="selectedAccount"
            :isEdit="editMode"
            @submit="onSubmit"
            @cancel="onCancel"
          />
        </JRow>

        <!-- SUPPLIER TABS -->
        <JTabs
          v-if="!profileStore.isMobileSubFormOpen"
          :tabItems="supplierItems"
          as="div"
          :defaultIndex="defaultIndex"
          tabOnly
          class="block bg-white focus:outline-none"
          tabListClass="w-100 border-1 my-16 flex rounded-md overflow-hidden border-line !space-x-0 [&>button:first-child]:rounded-s-md [&>button:last-child]:rounded-e-md"
          tabClass="w-full py-8 overflow-hidden"
          buttonClass="border border-line"
          selectedClass="bg-line font-medium"
          modelOptions
          @update:model-value="setSupplier"
        />

        <!-- Desktop Form -->
        <div
          v-if="
            !profileStore.isMobileSubFormOpen && !isMobile && tableItems.length
          "
        >
          <h3 class="my-16 text-subject font-bold">
            {{
              activeSupplierType &&
              $t(
                `profile.supplierTypes.${activeSupplierType.title.toLowerCase()}`
              )
            }}
            {{ $t('profile.suppliers') }}
          </h3>
          <JTable class="mt-40" :fields="tableFields" :items="tableItems">
            <template #cell(includeInBooking)="{ value }">
              <JIcon
                :name="value ? 'check-circle-bold-2' : 'close-circle-outline'"
                class="mx-auto"
                :class="[value ? 'text-success' : 'text-danger']"
              />
            </template>

            <template #cell(actions)="{ item }">
              <div class="flex items-center justify-end gap-8">
                <JButton
                  icon="eye"
                  color="secondary"
                  variant="tonal"
                  size="xs"
                  class="!text-secondary-800 hover:!text-secondary-800 focus:!text-secondary-800"
                  @click="onPreview(item.index)"
                />
                <JButton
                  icon="pen-bold"
                  color="secondary"
                  variant="tonal"
                  size="xs"
                  class="text-warning hover:text-warning focus:text-warning"
                  @click="onEdit(item.index)"
                />
                <JButton
                  icon="trash-bin"
                  color="secondary"
                  variant="tonal"
                  size="xs"
                  class="!text-danger hover:!text-danger focus:!text-danger"
                  @click="onConfirmDelete(item.index)"
                />
              </div>
            </template>
          </JTable>
          <div v-if="!profileStore.isMobileSubFormOpen && !isMobile && data">
            <div v-if="totalPages && totalPages > 1">
              <hr class="border-line pb-12 text-pane-header" />
              <JPagination
                v-model="page"
                justify="center"
                :totalPages="totalPages"
              />
            </div>
          </div>
        </div>

        <!-- FETCH LOADING -->
        <div v-if="isLoading" class="flex items-center justify-center px-16">
          <JIcon name="spinner" size="40" />
        </div>

        <!-- NO DATA -->
        <Transition
          enterFromClass="opacity-0"
          enterActiveClass="transition duration-300"
        >
          <FormNoEntries
            v-if="
              !isLoading &&
              !profileStore.isMobileSubFormOpen &&
              !tableItems.length
            "
            :text="$t('noData.form')"
            class="mt-16"
          />
        </Transition>

        <!-- Mobile Form -->
        <JBottomSheet
          v-if="isMobile"
          :modelValue="profileStore.isMobileSubFormOpen"
          disableSwipe
          hasPopstate
          :onBeforeClose="() => displayConfirmationModalIfTouched(false)"
          @update:model-value="displayConfirmationModalIfTouched"
        >
          <template #header>
            <JContainer
              v-if="!editMode"
              class="mt-16 px-16 text-pane-header font-bold"
            >
              {{ $t('words.new') }}
              {{
                activeSupplierType &&
                $t(
                  `profile.supplierTypes.${activeSupplierType.title.toLowerCase()}`
                )
              }}
              {{ $t('profile.suppliers').slice(0, -1) }}
            </JContainer>
            <JContainer v-else class="mt-16 px-16">
              <MyLoyaltyAccountsFormEditHeader
                :supplierName="tableItems[selected].supplierName"
              />
            </JContainer>
          </template>
          <JContainer class="p-16">
            <MyLoyaltyAccountsFormFields
              ref="formRef"
              :supplierType="activeSupplierType?.indicator!"
              :form="selectedAccount"
              :isEdit="editMode"
              @submit="onSubmit"
              @cancel="onCancel"
            />
          </JContainer>
        </JBottomSheet>

        <!-- Mobile List -->
        <ul
          v-if="isMobile && tableItems.length"
          class="mt-16 flex flex-col gap-y-8"
        >
          <li
            v-for="({ supplierName }, index) in tableItems"
            :key="index"
            class="flex items-center justify-between rounded-sm bg-white px-8 py-12 shadow-md"
          >
            <span class="text-base text-secondary">{{ supplierName }}</span>

            <span class="flex items-center justify-end gap-8">
              <JButton
                icon="eye"
                color="secondary"
                variant="tonal"
                size="xs"
                class="!text-secondary-800 hover:!text-secondary-800 focus:!text-secondary-800"
                @click="onPreview(index)"
              />
              <JButton
                icon="pen-bold"
                color="secondary"
                variant="tonal"
                size="xs"
                class="text-warning hover:text-warning focus:text-warning"
                @click="onEdit(index)"
              />
              <JButton
                icon="trash-bin"
                color="secondary"
                variant="tonal"
                size="xs"
                class="!text-danger hover:!text-danger focus:!text-danger"
                @click="onConfirmDelete(index)"
              />
            </span>
          </li>
        </ul>
        <div v-if="!profileStore.isMobileSubFormOpen && isMobile && data">
          <div v-if="totalPages && totalPages > 1">
            <hr class="border-line pb-12 text-pane-header" />
            <JPagination
              v-model="page"
              justify="center"
              :totalPages="totalPages"
            />
          </div>
        </div>
      </template>
    </template>

    <!-- DELETE CONFIRMATION -->
    <JConfirmation
      v-model="confirmationDeleteModal"
      :title="
        $t('profile.deleteTitle', { name: tableItems[selected]?.supplierName })
      "
      color="danger"
      :confirmText="$t('words.remove')"
      :cancelText="$t('words.cancel')"
      :isLoading="isLoadingDeleteMutation"
      @confirm="onDelete"
      @cancel="confirmationDeleteModal = false"
    />

    <PreviewModal v-model="preview" :title="$t('profile.myLoyaltyAccounts')">
      <template #body>
        <MyLoyaltyAccountPreview :loyaltyAccount="tableItems[selected]" />
      </template>
    </PreviewModal>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { JBottomSheet, JContainer, type JTabItem } from '@journey/ui-kit'
import {
  JButton,
  JConfirmation,
  JIcon,
  JPagination,
  JRow,
  JTable,
  JTabs,
} from '@journey/ui-kit'
import MyLoyaltyAccountsFormFields from './MyLoyaltyAccountsFormFields/MyLoyaltyAccountsFormFields.vue'
import MyLoyaltyAccountsFormEditHeader from './RateMySuppliersEditHeader/MyLoyaltyAccountsFormEditHeader.vue'
import FormNoEntries from '@/modules/Core/components/shared/FormNoEntries/FormNoEntries.vue'
import {
  FIRST_PAGE,
  INITIAL_PAGE_SIZE,
  MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY,
  updateProfileSectionSharedStateInjectionKey,
} from '@/modules/Profile/constants'
import { useDevicePlatform } from '@/modules/Core/composables'
import {
  useFetchLoyaltyAccountsQuery,
  useProfileQuery,
} from '@/modules/Profile/requests/queries'
import { usePrivateSupplierType } from '@/modules/Profile/composables/usePublicSupplierType'
import { useDeleteLoyaltyAccountMutation } from '@/modules/Profile/requests/mutations'
import useAuthStore from '@/modules/Auth/store'
import useProfileStore from '@/modules/Profile/store'
import PreviewModal from '@/modules/Profile/components/PreviewModal/PreviewModal.vue'
import MyLoyaltyAccountPreview from '@/modules/Profile/components/Forms/MyLoyaltyAccountsForm/MyLoyaltyAccountsPreview/MyLoyaltyAccountPreview.vue'
import { PublicSupplierTypeTitle } from '@/modules/Profile/types/model/publicSupplierType'
import featureFlag from '@/config/featureFlag'

const { t } = useI18n()
const { isMobile } = useDevicePlatform()

const profileStore = useProfileStore()

const authStore = useAuthStore()
const { data: profileData } = useProfileQuery({
  enabled: !authStore.user.name.first,
})

const hasUserProfileData = computed(
  () => !!(authStore.user.name.first || profileData.value?.data?.firstName)
)

// PUBLIC SUPPLIER TYPE
const {
  currentActiveType,
  supplierTypeItems,
  setSupplierType,
  isLoading: isLoadingSupplierTypes,
  activeSupplierType,
  enabled,
} = usePrivateSupplierType()

const supplierItems = computed(() => {
  const { car, flight, hotel } = featureFlag.activeResults

  return supplierTypeItems.value
    .filter((item) => {
      if (item.title === PublicSupplierTypeTitle.Car) {
        return car
      }
      if (item.title === PublicSupplierTypeTitle.Airline) {
        return flight
      }
      if (item.title === PublicSupplierTypeTitle.Hotel) {
        return hotel
      }
      return true
    })
    .map((item) => ({
      value: item.indicator,
      label: t(`profile.supplierTypes.${item.title.toLowerCase()}`),
    }))
})

const defaultIndex = ref(0)

function setSupplier(tab: JTabItem | number) {
  if (typeof tab === 'number') {
    return
  }

  defaultIndex.value = supplierTypeItems.value.findIndex(
    (item) => tab.value === item.indicator
  )

  setSupplierType(defaultIndex.value)
}

// TABLE
const page = ref(FIRST_PAGE)
const size = ref(INITIAL_PAGE_SIZE)

const { data, isLoading } = useFetchLoyaltyAccountsQuery(
  page,
  size,
  currentActiveType,
  {
    enabled,
  }
)
const totalPages = computed(() => data.value?.data.page.total)

const tableItems = computed(() => {
  if (!data.value?.data) {
    return []
  }

  return data.value?.data.items
})

const tableFields = computed(() => [
  {
    key: 'supplierName',
    label: t('profile.supplierName.table', {
      name:
        activeSupplierType.value &&
        t(
          `profile.supplierTypes.${activeSupplierType.value.title.toLowerCase()}`
        ),
    }),
  },
  {
    key: 'accountName',
    label: t('profile.loyaltyAccount.fields.accountName'),
  },
  {
    key: 'accountNumber',
    label: t('profile.loyaltyAccount.fields.accountNumber'),
  },
  {
    key: 'includeInBooking',
    label: t('profile.loyaltyAccount.fields.booking'),
  },
  {
    key: 'actions',
    label: t('words.actions'),
  },
])
const formRef = ref<InstanceType<typeof MyLoyaltyAccountsFormFields>>()
const editMode = ref(false)
const preview = ref(false)
const selected = ref(-1)

const selectedAccount = computed(() => {
  if (!editMode.value) {
    return tableItems.value[selected.value]
  }

  return {
    ...tableItems.value[selected.value],
    familyNumber: tableItems.value[selected.value].familyNumber || -1,
  }
})

const updateProfileSectionSharedState = inject(
  updateProfileSectionSharedStateInjectionKey
)

function onPreview(index: number) {
  selected.value = index
  preview.value = true
}

async function onSubmit() {
  profileStore.setIsMobileSubFormOpen(false)

  editMode.value = false
}

async function onEdit(index: number) {
  selected.value = index
  editMode.value = true

  profileStore.setIsMobileSubFormOpen(true)

  await nextTick()

  if (data.value?.data.items[index]) {
    return
  }

  formRef.value?.resetForm()

  profileStore.setIsMobileSubFormOpen(false)
}

async function onAdd() {
  selected.value = -1
  editMode.value = false

  profileStore.setIsMobileSubFormOpen(true)

  await nextTick()

  formRef.value?.resetForm()
}

function onCancel() {
  profileStore.setIsMobileSubFormOpen(false)

  editMode.value = false
}

// DELETE
const confirmationDeleteModal = ref(false)

const { isLoading: isLoadingDeleteMutation, mutateAsync: mutateAsyncDelete } =
  useDeleteLoyaltyAccountMutation(currentActiveType)

function onConfirmDelete(index: number) {
  selected.value = index
  confirmationDeleteModal.value = true
}

async function onDelete() {
  if (selected.value === null) {
    return
  }

  await mutateAsyncDelete({ ...tableItems.value[selected.value] })

  formRef.value?.resetForm()

  confirmationDeleteModal.value = false

  if (page.value === totalPages.value && data.value?.data.page.count === 1) {
    page.value -= 1
  }
}

// CONFIRMATION MODAL
function displayConfirmationModalIfTouched(value: boolean) {
  if (value) {
    profileStore.setIsMobileSubFormOpen(true)

    return true
  }

  if (
    updateProfileSectionSharedState!.items[
      MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY
    ].touched
  ) {
    profileStore.setIsConfirmModalOpen(true)

    return false
  }

  setTimeout(() => {
    profileStore.setIsMobileSubFormOpen(false)
  }, 100)

  return true
}

// SET ACTIVE FORM
function setActiveProfileForm(index: number) {
  if (isMobile.value) {
    return
  }

  profileStore.setActiveFormIndex(index)
}

onUnmounted(() => profileStore.setIsMobileSubFormOpen(false))
</script>
